import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, getApiError } from '../../lib/api';
import { Card } from '../../components/Card';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { EmptyState } from '../../components/EmptyState';
import { PageScaffold } from '../../components/PageScaffold';
import { DataTableCard } from '../../components/DataTable';
import { StatusPill } from '../../components/StatusPill';
import { FirmnessBadge } from '../../components/FirmnessBadge';
import { formatDayLabel, groupByDay, taskTone } from './opsShared';

// Bespoke custom-size IMPORTED mattresses that must be specially imported. They
// sit "awaiting import" until they arrive; only then does the order move to the
// delivery queue.
interface ImpRow {
  taskId: string;
  status: string;
  qty: number;
  orderId: string;
  invoice: string;
  shop: string;
  customer: string;
  phone: string;
  product: string;
  sku: string;
  customSize: string | null;
  fabric: string | null;
  density: string | null;
  supplierName: string | null;
  poReference: string | null;
  supplierNote: string | null;
  sentAt: string | null;
  readyAt: string | null;
  deliveryDate: string | null;
}

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'PENDING', label: 'Awaiting' },
  { key: 'SENT', label: 'Ordered' },
  { key: 'READY', label: 'Arrived' },
] as const;

const statusLabel = (s: string): string =>
  s === 'PENDING' ? 'Awaiting import' : s === 'SENT' ? 'Import ordered' : s === 'READY' ? 'Arrived' : s.toLowerCase();

type ModalState = { row: ImpRow } | null;

export function AwaitingImportPage(): JSX.Element {
  const qc = useQueryClient();
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('all');
  const [search, setSearch] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>(null);

  const list = useQuery({
    queryKey: ['ops-imports'],
    queryFn: () => apiGet<ImpRow[]>('/operations/imports'),
  });

  const update = useMutation({
    mutationFn: (vars: { taskId: string; body: Record<string, unknown> }) =>
      apiPost(`/operations/imports/${vars.taskId}/status`, vars.body),
    onSuccess: () => {
      setErr(null);
      setModal(null);
      qc.invalidateQueries({ queryKey: ['ops-imports'] });
    },
    onError: (e) => setErr(getApiError(e).message),
  });

  const counts = useMemo(() => {
    const rows = list.data ?? [];
    return {
      all: rows.length,
      PENDING: rows.filter((r) => r.status === 'PENDING').length,
      SENT: rows.filter((r) => r.status === 'SENT').length,
      READY: rows.filter((r) => r.status === 'READY').length,
    };
  }, [list.data]);

  const filtered = useMemo(() => {
    let rows = list.data ?? [];
    if (tab !== 'all') rows = rows.filter((r) => r.status === tab);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r) =>
          r.customer.toLowerCase().includes(q) ||
          r.invoice.toLowerCase().includes(q) ||
          r.product.toLowerCase().includes(q) ||
          (r.supplierName ?? '').toLowerCase().includes(q),
      );
    }
    return rows;
  }, [list.data, tab, search]);

  const groups = useMemo(() => groupByDay(filtered, (r) => r.deliveryDate), [filtered]);

  return (
    <PageScaffold
      scroll={false}
      title="Awaiting import"
      subtitle={
        <>
          Bespoke custom-size <strong>imported</strong> mattresses to bring in. Mark each{' '}
          <strong>Ordered</strong> when the import is placed, then <strong>Arrived</strong> once it
          lands — only then does the order move to delivery.
        </>
      }
    >
      {err && (
        <div className="shrink-0 rounded border border-negative/30 bg-negative-soft text-negative text-sm px-4 py-2">
          {err}
        </div>
      )}

      <div className="shrink-0 flex items-center justify-between gap-4 flex-wrap">
        <div className="inline-flex items-center gap-0.5 rounded-md border border-rule bg-surface p-0.5">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={
                'px-3 py-1.5 rounded text-sm transition ' +
                (tab === t.key ? 'bg-accent-soft text-accent font-medium' : 'text-ink-2 hover:bg-paper-2')
              }
            >
              {t.label}
              <span className="ml-1.5 text-xs text-ink-3">{counts[t.key]}</span>
            </button>
          ))}
        </div>
        <div className="w-64">
          <Input
            placeholder="Search customer, invoice, product…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <DataTableCard scrollClassName="space-y-6 p-px">
        {list.isLoading ? (
          <div className="text-center text-ink-3 py-10">Loading…</div>
        ) : groups.length === 0 ? (
          <EmptyState
            title="Nothing awaiting import"
            description="Confirmed custom-size imported mattresses appear here until they arrive."
          />
        ) : (
          groups.map((g) => (
            <Card
              key={g.day ?? 'unscheduled'}
              title={formatDayLabel(g.day)}
              subtitle={`${g.rows.length} item${g.rows.length === 1 ? '' : 's'}`}
              bodyClassName="p-0"
            >
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                    <th className="px-6 py-2 font-medium">Invoice</th>
                    <th className="px-6 py-2 font-medium">Product · custom size</th>
                    <th className="px-6 py-2 font-medium text-right">Qty</th>
                    <th className="px-6 py-2 font-medium">Import ref</th>
                    <th className="px-6 py-2 font-medium">Status</th>
                    <th className="px-6 py-2 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((r) => (
                    <tr key={r.taskId} className="border-b border-rule-2">
                      <td className="px-6 py-3">
                        <div className="kc-mono text-xs">{r.invoice}</div>
                        <div className="text-xs text-ink-3">{r.customer}</div>
                      </td>
                      <td className="px-6 py-3">
                        {r.product}
                        <div className="text-xs text-accent kc-mono">
                          {r.customSize ?? '—'}
                          {r.fabric ? ` · ${r.fabric}` : ''}
                        </div>
                        <FirmnessBadge value={r.density} className="mt-0.5" />
                      </td>
                      <td className="px-6 py-3 text-right kc-mono">{r.qty}</td>
                      <td className="px-6 py-3">
                        {r.supplierName ? (
                          <>
                            <div>{r.supplierName}</div>
                            {r.poReference && <div className="text-xs text-ink-3 kc-mono">{r.poReference}</div>}
                          </>
                        ) : (
                          <span className="text-xs text-ink-3">—</span>
                        )}
                      </td>
                      <td className="px-6 py-3">
                        <StatusPill tone={taskTone(r.status)}>{statusLabel(r.status)}</StatusPill>
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {r.status !== 'READY' && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => setModal({ row: r })}>
                                {r.status === 'PENDING' ? 'Mark ordered' : 'Edit'}
                              </Button>
                              <Button
                                size="sm"
                                loading={update.isPending && update.variables?.taskId === r.taskId}
                                onClick={() => update.mutate({ taskId: r.taskId, body: { status: 'READY' } })}
                              >
                                Mark arrived
                              </Button>
                            </>
                          )}
                          {r.status === 'READY' && <span className="text-xs text-positive">✓ arrived</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ))
        )}
      </DataTableCard>

      {modal && (
        <ImportModal
          row={modal.row}
          busy={update.isPending}
          onClose={() => setModal(null)}
          onSubmit={(body) => update.mutate({ taskId: modal.row.taskId, body })}
        />
      )}
    </PageScaffold>
  );
}

function ImportModal({
  row,
  busy,
  onClose,
  onSubmit,
}: {
  row: ImpRow;
  busy: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
}): JSX.Element {
  const [supplierName, setSupplierName] = useState(row.supplierName ?? '');
  const [poReference, setPoReference] = useState(row.poReference ?? '');
  const [note, setNote] = useState(row.supplierNote ?? '');

  const submit = (): void => {
    onSubmit({
      supplierName: supplierName.trim() || undefined,
      poReference: poReference.trim() || undefined,
      supplierNote: note.trim() || undefined,
      ...(row.status === 'PENDING' ? { status: 'SENT' } : {}),
    });
  };

  return (
    <Modal
      title="Import details"
      subtitle={`${row.product} · ${row.customSize ?? ''} · invoice ${row.invoice}`}
      onClose={onClose}
    >
      <div className="space-y-3">
        <Input label="Overseas supplier" placeholder="e.g. Sealy Intl" value={supplierName} onChange={(e) => setSupplierName(e.target.value)} autoFocus />
        <Input label="Import / shipment ref (optional)" placeholder="e.g. IMP-2026-0042" value={poReference} onChange={(e) => setPoReference(e.target.value)} />
        <Input label="Note (optional)" placeholder="anything to track" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <div className="flex gap-2 mt-5">
        <Button variant="secondary" className="flex-1" onClick={onClose}>
          Cancel
        </Button>
        <Button className="flex-1" loading={busy} onClick={submit}>
          {row.status === 'PENDING' ? 'Mark ordered' : 'Save'}
        </Button>
      </div>
    </Modal>
  );
}
